// Idle-session warning — "don't sign someone out with no warning."
//
// Grant, 2026-09-04: "if someone is literally in the middle of working it
// will auto log them out with no warning. this cant happen."
//
// The primary fix for that is server-side (workers/api/src/lib/session-
// renewal.ts): the idle window now genuinely restarts whenever you are
// active, so working never runs the clock out. This module handles what is
// left — the honest case where somebody really has been away, where the
// session SHOULD lapse and they should see it coming rather than lose a
// half-typed note to a sign-in screen.
//
// Two rules shape the whole design:
//
// 1. IT NEVER TALKS TO THE SERVER ON ITS OWN. A background poll would
//    renew the session as a side effect, and an idle timeout that a
//    forgotten open tab keeps alive forever is not an idle timeout. Every
//    renewal here traces back to something a person did — including the
//    "Stay signed in" button, which is a click.
//
// 2. IT ERRS EARLY, NEVER LATE. The exact expiry lives on the server, so
//    the client keeps a deliberately pessimistic LOWER BOUND (see
//    earliestExpiry). Warning a few minutes early costs a dismissible
//    banner; warning late means the warning did not happen.

/** Mirrors `expiresIn` in workers/api/src/auth.ts. */
export const IDLE_WINDOW_MS = 4 * 60 * 60 * 1000;

/** Mirrors `updateAge` in workers/api/src/auth.ts — the server renews at
 *  most once per this interval, which is the entire reason the client
 *  cannot know the exact expiry and must reason with a lower bound. */
export const RENEWAL_INTERVAL_MS = 5 * 60 * 1000;

/** How long before the (earliest possible) expiry to raise the banner. */
export const WARNING_LEAD_MS = 5 * 60 * 1000;

const STORAGE_KEY = "stewardship-os.last-activity";

export type IdlePhase = "active" | "warning" | "expired";

export interface IdleState {
  phase: IdlePhase;
  /** Milliseconds until the earliest possible expiry; 0 once past it. */
  msRemaining: number;
}

/**
 * The earliest moment the session could possibly lapse, given the last
 * request we know about.
 *
 * Why it is not simply `lastActivityAt + IDLE_WINDOW_MS`: the server only
 * re-stamps the expiry when a request lands more than RENEWAL_INTERVAL_MS
 * after the previous renewal. So a request at time T either renewed the
 * session (expiry = T + window) or found it too fresh to bother — which
 * means the last renewal was under RENEWAL_INTERVAL_MS before T, and the
 * expiry is at least T + window - renewalInterval. That second case is the
 * floor, so it is the one to plan against.
 */
export function earliestExpiry(lastActivityAt: number): number {
  return lastActivityAt + IDLE_WINDOW_MS - RENEWAL_INTERVAL_MS;
}

export function idleState(lastActivityAt: number, now: number): IdleState {
  const expiry = earliestExpiry(lastActivityAt);
  const msRemaining = Math.max(0, expiry - now);
  if (msRemaining === 0) return { phase: "expired", msRemaining: 0 };
  if (msRemaining <= WARNING_LEAD_MS) return { phase: "warning", msRemaining };
  return { phase: "active", msRemaining };
}

/** "4 minutes" / "45 seconds" — for a banner a person reads under mild
 *  time pressure, so it rounds up and never shows a bare "0 minutes". */
export function formatCountdown(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000));
  if (totalSeconds >= 60) {
    const minutes = Math.ceil(totalSeconds / 60);
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  return `${totalSeconds} second${totalSeconds === 1 ? "" : "s"}`;
}

// ── activity clock ────────────────────────────────────────────────────
//
// Kept in memory AND in localStorage. localStorage is the right home per
// the CLAUDE.md rule — high-churn transient state, meaningless on another
// device — and it is what makes this correct across tabs: a person working
// in a second tab is renewing the shared session cookie, and without the
// shared timestamp the first tab would sit there warning about a session
// that is in no danger at all.
//
// Every read and write is wrapped: private windows and blocked-site-data
// settings make these throw, and a warning banner is never worth taking
// the app down over. With no storage it degrades to per-tab memory, which
// is still correct — just chattier across tabs.

// Reached through globalThis rather than `window`, and typed to the two
// methods actually used. Same reason session-expiry.ts stays dependency-
// free: tests/web/*.test.ts is compiled by the ROOT tsconfig, which has no
// DOM lib, so a bare `window` here would break the typecheck for anything
// that imports this module. Absent storage (a Worker, a locked-down
// browser) simply reads as null.
type MinimalStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

function storage(): MinimalStorage | null {
  return (globalThis as { localStorage?: MinimalStorage }).localStorage ?? null;
}

let lastActivityInMemory = Date.now();

// Writing on every API call would be a lot of storage churn for a value
// only read at banner resolution; a 30s floor keeps it cheap without
// meaningfully aging the estimate.
const WRITE_THROTTLE_MS = 30 * 1000;
let lastWriteAt = 0;

export function recordActivity(at: number = Date.now()): void {
  if (at > lastActivityInMemory) lastActivityInMemory = at;
  if (at - lastWriteAt < WRITE_THROTTLE_MS) return;
  lastWriteAt = at;
  try {
    storage()?.setItem(STORAGE_KEY, String(at));
  } catch {
    // No storage — in-memory tracking still works for this tab.
  }
}

export function lastActivityAt(): number {
  let stored = 0;
  try {
    const raw = storage()?.getItem(STORAGE_KEY) ?? null;
    if (raw) {
      const parsed = Number(raw);
      // A clock that reads the future (a machine whose time was corrected
      // backwards, another tab on a skewed clock) would suppress the
      // warning entirely, so it is ignored rather than trusted.
      if (Number.isFinite(parsed) && parsed <= Date.now()) stored = parsed;
    }
  } catch {
    // No storage — fall through to the in-memory value.
  }
  return Math.max(stored, lastActivityInMemory);
}

/** Test seam — resets the module clock between cases. */
export function __resetActivityClock(at: number = Date.now()): void {
  lastActivityInMemory = at;
  lastWriteAt = 0;
}
